/**
 * JustificatifsAbsencePanel — item 12.3, côté staff.
 *
 * La file de ce qui attend une décision. Accepter fait basculer la
 * réservation en « absence justifiée » — et la politique de facturation déjà
 * réglée par la commune s'applique alors toute seule, sans qu'on touche au
 * calcul. Refuser exige un motif : sans lui, la famille ne sait pas quoi
 * refaire.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type AbsenceJustificatif } from '@/lib/api';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { AlertCircle, Check, FileText } from 'lucide-react';

const SERVICES: Record<string, string> = {
  cantine: 'Cantine', periscolaire: 'Périscolaire', alsh: 'ALSH', adulte: 'Repas adulte',
};

const STATUTS: Record<AbsenceJustificatif['statut'], { l: string; v: 'warning' | 'success' | 'urgent' }> = {
  en_attente: { l: 'À examiner', v: 'warning' },
  accepte: { l: 'Accepté', v: 'success' },
  refuse: { l: 'Refusé', v: 'urgent' },
};

function dateFr(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', weekday: 'short', day: 'numeric', month: 'short',
  }).format(new Date(`${iso}T12:00:00`));
}

export function JustificatifsAbsencePanel() {
  const [items, setItems] = useState<AbsenceJustificatif[]>([]);
  const [tout, setTout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [refus, setRefus] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try { setItems((await api.scolaire.justificatifs.aTraiter(!tout)).justificatifs); }
    catch { setItems([]); }
  }, [tout]);

  useEffect(() => { void charger(); }, [charger]);

  async function agir(fn: () => Promise<unknown>, ok: string) {
    setBusy(true); setError(null); setMsg(null);
    try { await fn(); setMsg(ok); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.payload.error : 'Action impossible'); }
    finally { setBusy(false); }
  }

  async function ouvrirDocument(id: string) {
    try {
      const { url } = await api.scolaire.justificatifs.document(id);
      window.open(url, '_blank', 'noopener');
    } catch { setError('Document indisponible'); }
  }

  return (
    <Card variant="glass" padded>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-ink">Justificatifs d'absence</h3>
        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
          <input type="checkbox" checked={tout} onChange={(e) => setTout(e.target.checked)} />
          Voir aussi les traités
        </label>
      </div>
      <p className="mb-3 text-xs text-ink-muted">
        Accepter marque l'absence comme justifiée : elle ne sera pas facturée,
        selon la règle que la commune a réglée dans les paramètres de la cantine.
      </p>

      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {tout ? 'Aucun justificatif.' : 'Aucun justificatif en attente.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((j) => (
            <li key={j.id} className="rounded-r-md border border-border/60 px-2.5 py-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-medium text-ink">{j.personne ?? 'Absence'}</span>
                <Badge variant="neutral" size="sm">{SERVICES[j.cibleType] ?? j.cibleType}</Badge>
                <Badge variant={STATUTS[j.statut].v} size="sm">{STATUTS[j.statut].l}</Badge>
                <span className="text-xs text-ink-muted">{dateFr(j.dateService)}</span>
              </div>
              <p className="mt-0.5 text-xs text-ink">
                {j.motif}
                {j.deposeParNom ? <span className="text-ink-muted"> — déposé par {j.deposeParNom}</span> : null}
              </p>
              {j.statut === 'refuse' && j.commentaire && (
                <p className="mt-0.5 text-xs text-danger">Refusé : {j.commentaire}</p>
              )}

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => void ouvrirDocument(j.id)}>
                  <FileText className="h-4 w-4" aria-hidden="true" /> {j.nomFichier}
                </Button>
                {j.statut === 'en_attente' && (
                  <>
                    <Button variant="primary" size="sm" disabled={busy}
                      onClick={() => void agir(
                        () => api.scolaire.justificatifs.decider(j.id, true),
                        'Absence justifiée — elle ne sera pas facturée.')}>
                      Accepter
                    </Button>
                    <input value={refus[j.id] ?? ''}
                      onChange={(e) => setRefus({ ...refus, [j.id]: e.target.value })}
                      placeholder="Motif du refus…" aria-label="Motif du refus"
                      className="h-8 min-w-44 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                    <Button variant="ghost" size="sm" disabled={busy || !(refus[j.id] ?? '').trim()}
                      onClick={() => void agir(
                        () => api.scolaire.justificatifs.decider(j.id, false, refus[j.id]),
                        'Justificatif refusé — la famille est prévenue.')}>
                      Refuser
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
